import React from 'react';
import { Spinner } from 'reactstrap';
import { httpClient } from '@s20/services/httpClient';
import { ApiRoutes } from '../Common';
import { FormLabel } from '../common/forms/FormElements';
import ValidatedSelect from '../common/forms/ValidatedSelect';

const getOption = (item) => {
    const ret = { value: item.id, label: item.companyName.trim() };

    return ret;
};

// Value must always be a string.  Options are objects with label/value properties, values are strings.
const getOptionValue = (opt) => opt.value;

const getUpdatedSubOptions = (allValidSubs) => {
    if (!allValidSubs) return null;

    // We want the distinct list of subs by id.
    // There is some overlap in subcontractors among companies.
    const idSet = new Set();

    const finalSubs = allValidSubs.filter((sub) => {
        const id = sub.value;
        const inSet = idSet.has(id);
        idSet.add(id);
        return !inSet;
    });

    return finalSubs;
};

const getOptions = (companies) => {
    const opts = companies.map((company) => {
        const c = getOption(company);
        c.subcontractors = company.subcontractors.map((sub) => getOption(sub));
        return c;
    });

    return opts;
};

// Assumes label is non-null string.
const sortOpts = (x, y) => {
    const ret = x.label.localeCompare(y.label);
    return ret;
};

export default class CompanySubSelects extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            companies: null,
            companiesLoading: true,
            selectedCompanies: null,
            currentSubs: null,
            selectedSubs: null,
        };

        this.companyChanged = this.companyChanged.bind(this);
        this.subcontractorChanged = this.subcontractorChanged.bind(this);
    }

    componentDidMount() {
        return this.populateState();
    }

    // Can use have an additional parameter (like "e") and use e.action to get the name of the event.
    companyChanged(opts) {
        // Opts will be null when you remove the last option.
        this.setState({ selectedCompanies: opts });

        const subs = opts
            ? opts.flatMap((x) => x.subcontractors).sort(sortOpts)
            : null;

        const currentSubs = getUpdatedSubOptions(subs);
        this.setState({ currentSubs });
        if (this.props.onCompaniesChange) this.props.onCompaniesChange(opts);

        this.pruneSelectedSubs(currentSubs);
    }

    populateState = async () => {
        this.props.onLoading(true);

        const companiesRaw = await httpClient.js(
            ApiRoutes.chargeView.hoursReportCompanies(),
        );

        const companyOpts = getOptions(companiesRaw).sort(sortOpts);

        this.setState({ companies: companyOpts, companiesLoading: false });

        this.props.onLoading(false);
    };

    pruneSelectedSubs(currentSubs) {
        const { selectedSubs } = this.state;

        let selSubs = null;

        // If there are no currentSubs or no selectedSubs, selectedSubs must be null.
        // If we have no currentSubs and we do have selectedSubs, we'd need to clear them all, in other words.
        // And if we didn't have selectedSubs in the first place, there's really no change.
        if (selectedSubs && currentSubs) {
            const currentIds = new Set(currentSubs.map((c) => c.value));

            // Remove any selectedSubs that are not in currentSubs.
            selSubs = selectedSubs.filter((sub) => currentIds.has(sub.value));
        }

        this.setState({ selectedSubs: selSubs });
        if (this.props.onSubcontractorsChange) this.props.onSubcontractorsChange(selSubs);
    }

    subcontractorChanged(opts) {
        let selSubs = null;
        if (opts) selSubs = opts.sort(sortOpts);

        this.setState({ selectedSubs: selSubs });
        if (this.props.onSubcontractorsChange) this.props.onSubcontractorsChange(selSubs);
    }

    render() {
        const {
            companies,
            selectedCompanies,
            currentSubs,
            selectedSubs,
            companiesLoading,
        } = this.state;

        const showSubcontractors = Boolean(
            !companiesLoading
            && selectedCompanies
            && selectedCompanies.length > 0,
        );

        return (
            <>
                <div className="col">
                    {companiesLoading ? (
                        <Spinner
                            type="border"
                            tag="div"
                            size="sm"
                            aria-hidden="true"
                        />
                    ) : (
                        <ValidatedSelect
                            id="companySubCompanies"
                            name="companySubCompanies"
                            label="Company"
                            isMulti
                            options={companies}
                            value={selectedCompanies}
                            onChange={this.companyChanged}
                            getOptionValue={getOptionValue}
                            onSelectAll={() => this.companyChanged(this.state.companies)}
                        />
                    )}
                </div>

                <div className="col">
                    <FormLabel
                        htmlFor="companySubSubcontractors"
                        label="Subcontractor"
                    />

                    {showSubcontractors ? (
                        <ValidatedSelect
                            id="companySubSubcontractors"
                            name="companySubSubcontractors"
                            isMulti
                            options={currentSubs}
                            value={selectedSubs}
                            onChange={this.subcontractorChanged}
                            getOptionValue={getOptionValue}
                            onSelectAll={() => this.subcontractorChanged(
                                this.state.currentSubs,
                            )}
                        />
                    ) : (
                        <div>Please select one or more companies.</div>
                    )}

                    {companiesLoading && (
                        <Spinner
                            type="border"
                            tag="div"
                            size="sm"
                            aria-hidden="true"
                        />
                    )}
                </div>
            </>
        );
    }
}
